import {
  savePatentApplicationDraft,
  updatePatentApplicationDraft
} from "./patentApplicationService"
import {
  createApplicant,
  updateApplicant,
  deleteApplicant
} from "./applicantService"
import {
  createPriority,
  updatePriority,
  deletePriority
} from "./priorityService"
import {
  createSignature,
  updateSignature,
  deleteSignature
} from "./signatureService"

function toIsoOrNull(value: string | null | undefined) {
  if (!value) return null
  const d = new Date(value)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

// Handles what SCRUM-43 (Anmelder und Erfinder), SCRUM-44 (Titel der
// Erfindung), SCRUM-45 (Priorität), SCRUM-47 (Unterlagen), SCRUM-48
// (Sprache), SCRUM-49 (Vertreter), SCRUM-50 (Staaten), SCRUM-52
// (Erfinderbenennung) and SCRUM-55 (Abgleich mit dem echten EPO-Tool:
// Sprache der Anmeldung, Titelübersetzungen, Art. 14(4) EPÜ, the
// remaining "Allgemeine Angaben" declarations, and the reworked
// Anmelder-/Erfinder-Formulare — origin-of-right is now carried on each
// inventor) cover: reference number, title, applicants and
// priorities (both synced to real documents, same pattern as
// Design/Trademark), inventors (with their per-inventor origin-of-right
// statement), designation states, and documents (all
// embedded directly on the PatentApplication — documents are already
// uploaded individually via patentDocumentService before this runs, so
// only their metadata is carried through here), the filing language (a
// Language sub-document, same pattern as referenceNumber), and the
// representative — a fixed value, same pattern (and same entity) as
// saveDraftService.ts/saveDesignDraftService.ts: the platform is the
// applicant's representative on every filing, so this isn't a user
// choice. Signature (SCRUM-53) is saved through the shared Signature
// resource, same pattern as applicants/priorities.
export async function savePatentDraft(params: {
  procedureType?: "EP1001" | "EP1200"
  epPhase?: any
  referenceNumber: string
  title?: string
  titleTranslations?: Record<string, string>
  applicants?: any[] | null
  inventors?: any[]
  priorities?: any[] | null
  designationStates?: any
  documents?: any[] | null
  filingLanguage?: string
  applicationLanguage?: string
  examinationInNonOfficialLanguage?: boolean
  declarations?: {
    divisionalApplication: boolean
    entitledPersonFiling: boolean
    referenceFiling: boolean
    waiverOfInvitationToProceed: boolean
    feeReductionNaturalPersonOrEntity: boolean
    feeReductionMicroEntity: boolean
  }
  claimsFiledLater?: boolean
  translationFiledLater?: boolean
  numberOfClaims?: number | null
  signature?: any | null
  draftId?: string | null
  signatureId?: string | null
  onDraftSaved?: (id: string) => void
  onSignatureSaved?: (id: string | null) => void
}) {
  const {
    procedureType,
    epPhase,
    referenceNumber,
    title,
    titleTranslations,
    applicants,
    inventors,
    priorities,
    designationStates,
    documents,
    filingLanguage,
    applicationLanguage,
    examinationInNonOfficialLanguage,
    declarations,
    claimsFiledLater,
    translationFiledLater,
    numberOfClaims,
    signature,
    draftId,
    signatureId,
    onDraftSaved,
    onSignatureSaved
  } = params

  const savedApplicantIds: string[] = []
  let savedSignatureId = signatureId || null

  if (applicants && Array.isArray(applicants)) {
    for (const applicant of applicants) {
      if (applicant._id && applicant.isDeleted) {
        await deleteApplicant(applicant._id)
        continue
      }

      if (applicant._id && !applicant.isDeleted) {
        const updated = await updateApplicant(applicant._id, applicant)
        savedApplicantIds.push(updated._id)
        continue
      }

      if (!applicant._id && !applicant.isDeleted) {
        const created = await createApplicant(applicant)
        savedApplicantIds.push(created._id)
        applicant._id = created._id
      }
    }
  }

  const savedPriorityIds: string[] = []

  if (priorities && Array.isArray(priorities)) {
    for (const priority of priorities) {
      const data = {
        office: priority.office,
        filingNumber: priority.filingNumber,
        filingDate: toIsoOrNull(priority.filingDate)
      }

      if (priority._id && priority.isDeleted) {
        await deletePriority(priority._id)
        continue
      }

      if (priority._id && !priority.isDeleted) {
        const updated = await updatePriority(priority._id, data)
        savedPriorityIds.push(updated._id)
        continue
      }

      if (!priority._id && !priority.isDeleted) {
        const created = await createPriority(data)
        savedPriorityIds.push(created._id)
        priority._id = created._id
      }
    }
  }

  const hasSignature =
    signature &&
    signature.accepted &&
    signature.signerName

  if (!hasSignature) {
    if (savedSignatureId) await deleteSignature(savedSignatureId)
    savedSignatureId = null
    onSignatureSaved?.(null)
  } else if (savedSignatureId) {
    const updated = await updateSignature(savedSignatureId, {
      signerName: signature.signerName
    })
    savedSignatureId = updated._id
    onSignatureSaved?.(savedSignatureId)
  } else {
    const created = await createSignature({
      signerName: signature.signerName
    })
    savedSignatureId = created._id
    onSignatureSaved?.(savedSignatureId)
  }

  const payload = {
    procedureType: procedureType || "EP1001",
    epPhase: epPhase || null,
    referenceNumber,
    title: title || "",
    titleTranslations: titleTranslations && Object.keys(titleTranslations).length
      ? titleTranslations
      : null,
    applicants: savedApplicantIds,
    inventors: inventors || [],
    priorities: savedPriorityIds,
    designationStates: designationStates || null,
    documents: documents || [],
    languages: filingLanguage ? { filingLanguage, secondLanguage: null } : null,
    applicationLanguage: applicationLanguage || filingLanguage || "",
    examinationInNonOfficialLanguage: Boolean(examinationInNonOfficialLanguage),
    declarations: declarations || null,
    claimsFiledLater: Boolean(claimsFiledLater),
    translationFiledLater: Boolean(translationFiledLater),
    numberOfClaims: numberOfClaims ?? null,
    signature: savedSignatureId,
    representative: {
      name: "DANTZ IP & LAW",
      firstName: "Dirk",
      lastName: "Dantz",
      street: "Kurfürstendamm 43D",
      postalCode: "10719",
      city: "Berlin",
      country: "Deutschland",
      countryShort: "DE"
    }
  }

  if (draftId) {
    await updatePatentApplicationDraft(draftId, payload)
    return draftId
  }

  const created = await savePatentApplicationDraft(payload)
  onDraftSaved?.(created._id)
  return created._id
}
